import { Modal as MaterialModal } from '@material-ui/core';
import { PAYMENT_TYPES } from '@nandosaus/constants';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Alert } from '@components/alert';
import { Button } from '@components/button/new';
import { Box, Flex } from '@components/grid';
import { Icon } from '@components/icons';
import { Image } from '@components/image';
import { P } from '@components/typography/new';
import { SignInForm } from '@components/sign-in-form';
import { breakpoints, colors, navigation } from '@constants';
import { useExpressCheckout } from '@hooks/use-express-checkout';
import { setLandingPage } from '../../utils/auth';

const WALLETS = {
  [PAYMENT_TYPES.APPLE_PAY]: { label: 'Apple Pay', mark: '/static/images/express-checkout/apple-pay-mark.svg' },
  [PAYMENT_TYPES.GOOGLE_PAY]: { label: 'Google Pay', mark: '/static/images/express-checkout/google-pay-mark.svg' },
};

const PERKS_BENEFITS = ['Make changes to your order online', 'Easy reordering', 'Access to exclusive member offers'];

const WalletButton = ({ walletPaymentType, onClick, isLoading, disabled }) => {
  const { label, mark } = WALLETS[walletPaymentType];

  return (
    <Button
      variant="secondary"
      size="tall"
      widthType="full"
      onClick={onClick}
      isLoading={isLoading}
      loadingText="Placing order"
      disabled={disabled}
      aria-label={`Checkout with ${label}`}
      data-testid={`express-checkout-${walletPaymentType}`}
    >
      <Flex alignItems="center" gap="8px">
        Checkout with
        <Image src={mark} alt="" width="51px" height="20px" />
      </Flex>
    </Button>
  );
};

WalletButton.propTypes = {
  walletPaymentType: PropTypes.oneOf([PAYMENT_TYPES.APPLE_PAY, PAYMENT_TYPES.GOOGLE_PAY]).isRequired,
  onClick: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const PeriPerksHeading = ({ children, variant, ...rest }) => (
  <Flex alignItems="center" gap="8px" {...rest}>
    <Icon name="periPerksLogo" height="23px" width="30px" stroke="none" />
    <P variant={variant}>{children}</P>
  </Flex>
);

PeriPerksHeading.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.string,
};

PeriPerksHeading.defaultProps = {
  variant: 'medium-semi-bold',
};

const ExpressCheckoutOptions = observer(({ onSignInClick }) => {
  const { CartStore } = useRootStore();
  const router = useRouter();
  const { walletPaymentTypes, isSubmitting, walletError, payWithWallet, continueAsGuest } = useExpressCheckout();
  const [selectedWallet, setSelectedWallet] = useState(null);

  const handleCreateAccount = () => {
    setLandingPage(navigation.CHECKOUT);
    router.push(navigation.CREATE_ACCOUNT);
  };

  return (
    <>
      <Flex
        flexDirection="column"
        gap="12px"
        width="100%"
        px="12px"
        py="26px"
        borderRadius="16px"
        backgroundColor={colors.warmWhite3}
      >
        <Flex flexDirection="column" gap="12px" pb="8px">
          <PeriPerksHeading pl="8px">Checkout with PERi-Perks</PeriPerksHeading>
          <BenefitsList>
            {PERKS_BENEFITS.map(benefit => (
              <P as="li" key={benefit} variant="standard">
                {benefit}
              </P>
            ))}
          </BenefitsList>
        </Flex>
        <Button variant="primary" size="tall" widthType="full" onClick={onSignInClick} disabled={isSubmitting}>
          Sign in
        </Button>
        <Button variant="tertiary" size="tall" widthType="full" onClick={handleCreateAccount} disabled={isSubmitting}>
          Create account
        </Button>
      </Flex>

      {CartStore.isGuestOrderingAvailable && (
        <>
          <P variant="small-semi-bold" textAlign="center">
            Or checkout as guest
          </P>
          {walletError && (
            <Alert type="error" width="100%">
              {walletError}
            </Alert>
          )}
          {walletPaymentTypes.map(walletPaymentType => (
            <WalletButton
              key={walletPaymentType}
              walletPaymentType={walletPaymentType}
              onClick={() => {
                setSelectedWallet(walletPaymentType);
                payWithWallet(walletPaymentType);
              }}
              isLoading={isSubmitting && selectedWallet === walletPaymentType}
              disabled={isSubmitting}
            />
          ))}
          <Button
            variant="tertiary"
            size="tall"
            widthType="full"
            onClick={continueAsGuest}
            disabled={isSubmitting}
            data-testid="express-checkout-continue-as-guest"
          >
            Continue as guest
          </Button>
        </>
      )}
    </>
  );
});

ExpressCheckoutOptions.propTypes = {
  onSignInClick: PropTypes.func.isRequired,
};

const ExpressCheckoutSignIn = observer(({ onSignedIn }) => {
  const { MemberStore } = useRootStore();
  const { profile } = MemberStore;

  // Close the sheet once signed in, leaving the customer on their (now member) cart. Members without
  // a mobile number are shown SignInForm's update-details step first, so wait until that's done too.
  useEffect(() => {
    if (profile?.mobile) {
      onSignedIn();
    }
  }, [profile, onSignedIn]);

  return (
    <Box width="100%" pb="16px">
      <SignInForm
        headingText={
          <PeriPerksHeading variant="medium-bold" mb="24px">
            Sign in to your account
          </PeriPerksHeading>
        }
        landingPage={navigation.CHECKOUT}
        hideCreateAccountLink
        noXPadding
        isCheckout
      />
    </Box>
  );
});

ExpressCheckoutSignIn.propTypes = {
  onSignedIn: PropTypes.func.isRequired,
};

/**
 * Bottom sheet shown to signed-out users when they continue to checkout from the cart: sign in /
 * create an account for PERi-Perks, or check out as a guest — express with the device's wallet
 * (Apple Pay or Google Pay) or via the standard guest checkout form.
 *
 * Full width on mobile; on desktop it sits at the bottom of the 520px cart drawer.
 */
const ExpressCheckoutModal = ({ open, onClose }) => {
  const [isSigningIn, setIsSigningIn] = useState(false);

  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.recaptcha.siteKeyV3}>
      <StyledMaterialModal open={open} onClose={onClose}>
        <Sheet data-testid="express-checkout-modal" role="dialog" aria-label="Checkout options">
          {isSigningIn ? (
            <ExpressCheckoutSignIn onSignedIn={onClose} />
          ) : (
            <ExpressCheckoutOptions onSignInClick={() => setIsSigningIn(true)} />
          )}
        </Sheet>
      </StyledMaterialModal>
    </GoogleReCaptchaProvider>
  );
};

ExpressCheckoutModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const StyledMaterialModal = styled(MaterialModal)`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;

  @media (max-width: ${breakpoints.md}) {
    justify-content: center;
  }
`;

const Sheet = styled(Flex)`
  @keyframes slideUpFromBottom {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 520px;
  max-height: 100%;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  border-radius: 24px 24px 0 0;
  background-color: ${colors.warmWhite2};
  outline: none;
  animation: slideUpFromBottom 0.3s ease-in-out;

  @media (max-width: ${breakpoints.md}) {
    width: 100%;
  }
`;

const BenefitsList = styled.ul`
  margin: 0;
  padding-left: 27px;
  list-style: disc;
`;

export { ExpressCheckoutModal };
